import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { Component } from 'react'

import IconText from '@/app/component/layout/icon-text'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import { hasValidTTSContent } from '@/app/module/campaigns/campaign-validation-helpers'
import Voice from '@/app/module/campaigns/component/item/steps/content/voice'
import { LanguageType } from '@/app/module/campaigns/helpers'
import { LocalPlayAudioAction, VoiceProps } from '@/app/module/campaigns/types'

type Props = {
  defaultLanguage: string
  languages: LanguageType[]
  value: LocalPlayAudioAction
  voiceProps: VoiceProps

  onClose: () => void
  onSave: (value: Partial<LocalPlayAudioAction>) => void
}

type State = {
  audio: LocalPlayAudioAction['play']
  error: string
}

export default class PlayAudio extends Component<Props, State> {
  state = {
    audio: this.props.value.play,
    error: '',
  }

  isSaveDisabled = () => {
    const { audio, error } = this.state
    const isAudioFile = 'playfile' in audio && !!audio.playfile
    return !!error || !audio || (isAudioFile && !audio.playfile) || (!isAudioFile && !hasValidTTSContent(audio))
  }

  render() {
    const { voiceProps, onSave, onClose, languages, defaultLanguage } = this.props
    const { audio } = this.state

    return (
      <CustomDialog open={true} onClose={onClose}>
        <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <IconText>
              <Icon>phone</Icon>
              <Typography variant="h6">Play audio message</Typography>
            </IconText>
            <IconButton onClick={onClose}>
              <Icon>close</Icon>
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {shouldShowContentWarning(audio, languages) && (
            <Alert severity="warning">{getContentWarningMessage(audio)}</Alert>
          )}
          <div
            style={{
              width: '70vw',
              minWidth: '300px',
              maxWidth: '100%',
            }}
          >
            <Voice
              {...voiceProps}
              languages={languages}
              defaultLanguage={defaultLanguage}
              enableTranscription
              enableTTS
              info=""
              item={{
                id: voiceProps.id,
                audio,
              }}
              changeHandler={(item: { audio: LocalPlayAudioAction['play'] }) => {
                this.setState({ audio: item.audio })
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            disabled={this.isSaveDisabled()}
            onClick={() => {
              if (!audio) {
                return
              }

              onSave({
                play: audio,
              })
            }}
          >
            Save
          </Button>
        </DialogActions>
      </CustomDialog>
    )
  }
}

const hasDefaultAudio = (audio: LocalPlayAudioAction['play']) => 'playfile' in audio && !!audio.playfile

const hasDefaultTTS = (audio: LocalPlayAudioAction['play']) => 'say' in audio && !!audio.say && !!audio.voice

const hasTranslatedAudio = (audio: LocalPlayAudioAction['play']) =>
  Object.values(audio.translations || {}).some((translation) => !!translation.playfile)

const hasTranslatedTTS = (audio: LocalPlayAudioAction['play']) =>
  Object.values(audio.translations || {}).some((translation) => !!translation.say && !!translation.voice)

const shouldShowContentWarning = (audio: LocalPlayAudioAction['play'] | undefined, languages: LanguageType[]) =>
  !!audio &&
  languages.length > 0 &&
  ((hasTranslatedAudio(audio) && !hasDefaultAudio(audio)) || (hasTranslatedTTS(audio) && !hasDefaultTTS(audio)))

const getContentWarningMessage = (audio: LocalPlayAudioAction['play']) => {
  if (hasTranslatedAudio(audio) && !hasDefaultAudio(audio)) {
    return 'You must add audio for the default language before you can save this action.'
  }

  if (hasTranslatedTTS(audio) && !hasDefaultTTS(audio)) {
    return 'You must add Text-to-Speech content for the default language before you can save this action.'
  }

  return ''
}
